import React, { useState, useCallback, useMemo, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Alert,
  Box,
  Button,
  Chip,
  Checkbox,
  CircularProgress,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  IconButton,
  LinearProgress,
  Paper,
  Radio,
  Tab,
  Tabs,
  TextField as MuiTextField,
  Tooltip,
} from "@mui/material";
import {
  SearchOutlined,
  CheckCircleOutline,
  RefreshOutlined,
  PauseCircleOutlined,
  SendOutlined,
  DescriptionOutlined,
  WarningAmberOutlined,
  ChatBubbleOutline,
  HistoryOutlined,
  PeopleOutline,
  CloudUploadOutlined,
  ExpandMore,
  ExpandLess,
} from "@mui/icons-material";
import {
  orange,
  green,
  red,
  blue,
  grey,
  amber,
  indigo,
  purple,
  teal,
} from "@mui/material/colors";
import isArray from "lodash/isArray";
import isNil from "lodash/isNil";

import Txt from "../../components/typography/Txt";
import Flx from "../../components/layout/Flx";
import { useReggoraRouteHook } from "../../hooks/useReggoraRouteHook";
import {
  REGGORA_EVENT_DISPLAY,
  REGGORA_STATUS_COLORS,
  REGGORA_TERMINAL_STATUSES,
} from "../../constants/reggoraConstants";
import { usePipelineHook } from "../../hooks/usePipelineHook";
import { loanPipelineSet } from "../../redux/actions/loanPipelineActions";
import SelectAutoInput from "../../components/inputs/SelectAutoInput";
import InputWrapper from "../../components/inputs/shared/InputWrapper";
import ScreenContent from "../../components/layout/ScreenContent";
import Htag from "../../components/typography/Htag";
import SelectMultipleInput from "../../components/inputs/SelectMultipleInput";
import SelectMultipleCheckboxInput from "../../components/inputs/SelectMultipleCheckboxInput";
import LoanSearchDropdown from "../../components/inputs/common/LoanSearchDropdown";

// ═══════════════════════════════════════════════════════════
// Constants
// ═══════════════════════════════════════════════════════════

const REGGORA_PRIORITIES = ["Normal", "Rush"];
const REGGORA_ALLOCATION_TYPES = ["automatically", "manually"];
const REGGORA_EVENT_TYPES = [
  "order_created",
  "order_updated",
  "status_update",
  "order_approved",
  "order_report_received",
  "order_ready_for_review",
  "order_report_sent",
  "consumer_payment_created",
  "consumer_payment_completed",
  "consumer_payment_updated",
  "consumer_payment_deleted",
];
const REGGORA_STATUSES = [
  "Finding Appraisers",
  "Accepted",
  "Inspection Scheduled",
  "Inspection Completed",
  "Under Review",
  "Revision Requested",
  "Revision Submitted",
  "Submitted",
  "Attention Required",
  "On Hold",
  "Waiting for Payment",
  "Canceled",
];
const PAYMENT_EVENT_TYPES = new Set([
  "consumer_payment_created",
  "consumer_payment_completed",
  "consumer_payment_updated",
  "consumer_payment_deleted",
]);

const EVENT_ICON_MAP = {
  order_created: { icon: "📋", color: green[100], textColor: green[800] },
  order_updated: { icon: "🔄", color: blue[100], textColor: blue[800] },
  status_update: { icon: "📊", color: indigo[100], textColor: indigo[800] },
  order_approved: { icon: "✅", color: green[100], textColor: green[800] },
  order_report_received: {
    icon: "📄",
    color: purple[100],
    textColor: purple[800],
  },
  order_ready_for_review: {
    icon: "👁",
    color: amber[100],
    textColor: amber[800],
  },
  order_report_sent: { icon: "📤", color: teal[100], textColor: teal[800] },
  consumer_payment_created: {
    icon: "💳",
    color: orange[100],
    textColor: orange[800],
  },
  consumer_payment_completed: {
    icon: "💰",
    color: green[100],
    textColor: green[800],
  },
  consumer_payment_updated: {
    icon: "💳",
    color: orange[100],
    textColor: orange[800],
  },
  consumer_payment_deleted: {
    icon: "❌",
    color: red[100],
    textColor: red[800],
  },
  hold: { icon: "⏸️", color: amber[100], textColor: amber[800] },
  conversation_message: { icon: "💬", color: blue[100], textColor: blue[800] },
};

const CONTACT_TYPE_COLORS = {
  attorney: { bg: purple[50], color: purple[700], border: purple[200] },
  lawyer: { bg: purple[50], color: purple[700], border: purple[200] },
  title: { bg: teal[50], color: teal[700], border: teal[200] },
  "title company": { bg: teal[50], color: teal[700], border: teal[200] },
  "title agent": { bg: teal[50], color: teal[700], border: teal[200] },
  realtor: { bg: orange[50], color: orange[700], border: orange[200] },
  "real estate agent": {
    bg: orange[50],
    color: orange[700],
    border: orange[200],
  },
  broker: { bg: orange[50], color: orange[700], border: orange[200] },
  insurance: { bg: "#e0f7fa", color: "#00838f", border: "#b2ebf2" },
  borrower: { bg: blue[50], color: blue[700], border: blue[200] },
  guarantor: { bg: indigo[50], color: indigo[700], border: indigo[200] },
  appraiser: { bg: amber[50], color: amber[700], border: amber[200] },
  contractor: { bg: red[50], color: red[700], border: red[200] },
  accountant: { bg: green[50], color: green[700], border: green[200] },
  cpa: { bg: green[50], color: green[700], border: green[200] },
};

// ═══════════════════════════════════════════════════════════
// Shared Helper Components
// ═══════════════════════════════════════════════════════════

const SectionPaper = ({ title, children, sx, ...props }) => (
  <Paper
    elevation={0}
    sx={{
      border: `1px solid ${grey[200]}`,
      borderRadius: "8px",
      overflow: "hidden",
      ...sx,
    }}
    {...props}
  >
    {title && (
      <Box
        sx={{
          background: "linear-gradient(135deg, #1565c0 0%, #1976d2 100%)",
          px: 3,
          py: 1.5,
        }}
      >
        <Txt bold sx={{ color: "#fff", fontSize: "16px" }}>
          {title}
        </Txt>
      </Box>
    )}
    <Box sx={{ p: 2.5 }}>{children}</Box>
  </Paper>
);

const StatusChip = ({ status }) => {
  const colorMap = {
    warning: { bg: orange[50], color: orange[800], border: orange[200] },
    info: { bg: blue[50], color: blue[800], border: blue[200] },
    success: { bg: green[50], color: green[800], border: green[200] },
    error: { bg: red[50], color: red[800], border: red[200] },
    secondary: { bg: purple[50], color: purple[800], border: purple[200] },
    default: { bg: grey[100], color: grey[700], border: grey[300] },
  };
  const muiColor = REGGORA_STATUS_COLORS[status] || "default";
  const colors = colorMap[muiColor] || colorMap.default;
  return (
    <Chip
      label={status}
      size="small"
      sx={{
        backgroundColor: colors.bg,
        color: colors.color,
        border: `1px solid ${colors.border}`,
        fontWeight: 600,
        fontSize: "12px",
      }}
    />
  );
};

const AvatarBadge = ({ name, bgColor, textColor }) => {
  const initials = name
    ? name
        .split(/\s+/)
        .map((w) => w[0])
        .join("")
        .toUpperCase()
        .slice(0, 2)
    : "?";
  return (
    <Box
      sx={{
        width: 32,
        height: 32,
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "12px",
        fontWeight: 700,
        flexShrink: 0,
        backgroundColor: bgColor || grey[200],
        color: textColor || grey[700],
      }}
    >
      {initials}
    </Box>
  );
};

const StepIndicator = ({ currentStep }) => {
  const steps = [
    { key: "lookup", label: "1. Lookup", order: 0 },
    { key: "creating", label: "2. Creating Loans", order: 1 },
    { key: "editing", label: "3. Edit Loans", order: 2 },
    { key: "submitting", label: "4. Submitting", order: 3 },
    { key: "streaming", label: "4. Progress", order: 3 },
    { key: "complete", label: "5. Done", order: 4 },
  ];
  const currentOrder =
    {
      lookup: 0,
      creating: 1,
      editing: 2,
      submitting: 3,
      streaming: 3,
      complete: 4,
      error: -1,
    }[currentStep] ?? -1;

  return (
    <Flx ac gap={0.5} sx={{ flexWrap: "wrap" }}>
      {steps.map((s) => {
        const isActive = currentOrder === s.order;
        const isDone = currentOrder > s.order;
        return (
          <Chip
            key={s.key}
            label={s.label}
            size="small"
            sx={{
              fontWeight: isActive ? 700 : 500,
              fontSize: "12px",
              ...(isActive && { backgroundColor: blue[600], color: "#fff" }),
              ...(isDone && { backgroundColor: green[50], color: green[700] }),
              ...(!isActive &&
                !isDone && { backgroundColor: grey[100], color: grey[400] }),
            }}
          />
        );
      })}
    </Flx>
  );
};

const Accordion = ({ title, defaultOpen = false, children }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Paper variant="outlined" sx={{ borderRadius: "8px", mb: 1.5 }}>
      <Box
        onClick={() => setOpen(!open)}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2,
          py: 1.5,
          cursor: "pointer",
          "&:hover": { backgroundColor: grey[50] },
        }}
      >
        <Txt body2 bold>
          {title}
        </Txt>
        {open ? (
          <ExpandLess sx={{ color: grey[400] }} />
        ) : (
          <ExpandMore sx={{ color: grey[400] }} />
        )}
      </Box>
      <Collapse in={open}>
        <Box sx={{ px: 2, pb: 2, borderTop: `1px solid ${grey[100]}` }}>
          {children}
        </Box>
      </Collapse>
    </Paper>
  );
};

// ═══════════════════════════════════════════════════════════
// Comments Panel (right column)
// ═══════════════════════════════════════════════════════════

const CommentsPanel = ({ notifications, contacts }) => {
  const contactMap = useMemo(() => {
    const m = {};
    (contacts || []).forEach((c) => {
      if (c.emailAddress) m[c.emailAddress.toLowerCase()] = c;
    });
    return m;
  }, [contacts]);

  if (!notifications || notifications.length === 0) {
    return (
      <Flx ac jc sx={{ py: 6 }}>
        <Txt body2 color="text.secondary" sx={{ fontStyle: "italic" }}>
          No notifications yet. Webhook events will appear here.
        </Txt>
      </Flx>
    );
  }

  const sorted = [...notifications].sort(
    (a, b) =>
      new Date(a.created_at || a.event_time || 0) -
      new Date(b.created_at || b.event_time || 0),
  );
  let lastDate = "";

  return (
    <Flx column>
      {sorted.map((notif, i) => {
        const evtConfig = EVENT_ICON_MAP[notif.event_type] || {
          icon: "📌",
          color: grey[100],
          textColor: grey[700],
        };
        const evtDisplay = REGGORA_EVENT_DISPLAY[notif.event_type] || {
          label: notif.event_type,
        };
        const time = notif.created_at || notif.event_time || "";
        const timeStr = time ? new Date(time).toLocaleString() : "";
        const dateLabel = time
          ? new Date(time).toLocaleDateString("en-US", {
              weekday: "long",
              month: "numeric",
              day: "numeric",
              year: "numeric",
            })
          : "";
        const showDateSep = dateLabel && dateLabel !== lastDate;
        if (dateLabel) lastDate = dateLabel;

        // Parse @mentions from email addresses
        const message = notif.message || "";
        const emailRegex = /([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/g;
        const parts = message.split(emailRegex);
        const rendered = parts.map((part, pi) => {
          if (emailRegex.test(part)) {
            emailRegex.lastIndex = 0;
            const contact = contactMap[part.toLowerCase()];
            const name = contact
              ? `${contact.firstName || ""} ${contact.lastName || ""}`.trim() ||
                part
              : part;
            return (
              <Chip
                key={pi}
                label={`@${name}`}
                size="small"
                sx={{
                  height: 20,
                  fontSize: "11px",
                  fontWeight: 600,
                  backgroundColor: blue[50],
                  color: blue[700],
                  mx: 0.25,
                }}
              />
            );
          }
          return <span key={pi}>{part}</span>;
        });

        return (
          <React.Fragment key={i}>
            {showDateSep && (
              <Flx ac gap={1} sx={{ py: 1, px: 2 }}>
                <Divider sx={{ flex: 1 }} />
                <Txt
                  caption
                  color="text.secondary"
                  sx={{ whiteSpace: "nowrap" }}
                >
                  {dateLabel}
                </Txt>
                <Divider sx={{ flex: 1 }} />
              </Flx>
            )}
            <Flx
              gap={1.5}
              sx={{
                py: 1.5,
                px: 1.5,
                "&:hover": { backgroundColor: grey[50] },
                borderRadius: 1,
                transition: "background-color 0.15s",
              }}
            >
              <AvatarBadge
                name={evtDisplay.label}
                bgColor={evtConfig.color}
                textColor={evtConfig.textColor}
              />
              <Flx column sx={{ flex: 1, minWidth: 0 }}>
                <Flx ac gap={1} sx={{ mb: 0.25 }}>
                  <Txt body2 bold sx={{ fontSize: "13px" }}>
                    {evtConfig.icon} {evtDisplay.label}
                  </Txt>
                  <Txt caption color="text.secondary">
                    {timeStr}
                  </Txt>
                </Flx>
                <Txt
                  body2
                  color="text.secondary"
                  sx={{ fontSize: "13px", lineHeight: 1.5 }}
                >
                  {rendered}
                </Txt>
                {notif.event_data?.status && (
                  <Box sx={{ mt: 0.5 }}>
                    <StatusChip status={notif.event_data.status} />
                  </Box>
                )}
                {notif.email_payload && (
                  <Box
                    sx={{
                      mt: 1,
                      p: 1.5,
                      backgroundColor: blue[50],
                      border: `1px solid ${blue[200]}`,
                      borderRadius: 1,
                    }}
                  >
                    <Txt caption bold sx={{ color: blue[700] }}>
                      Email queued
                    </Txt>
                    <Txt caption sx={{ color: blue[600] }}>
                      To: {notif.email_payload.to}
                    </Txt>
                    <Txt caption sx={{ color: blue[600] }}>
                      Subject: {notif.email_payload.subject}
                    </Txt>
                    {notif.email_payload.attachments?.length > 0 && (
                      <Txt caption sx={{ color: blue[500], mt: 0.5 }}>
                        📎 {notif.email_payload.attachments.length}{" "}
                        attachment(s)
                      </Txt>
                    )}
                  </Box>
                )}
              </Flx>
            </Flx>
            {i < sorted.length - 1 && <Divider sx={{ mx: 1.5 }} />}
          </React.Fragment>
        );
      })}
    </Flx>
  );
};

// ═══════════════════════════════════════════════════════════
// Webhook History Panel (right column)
// ═══════════════════════════════════════════════════════════

const WebhookHistoryPanel = ({ events }) => {
  const [expandedIdx, setExpandedIdx] = useState(null);
  if (!events || events.length === 0)
    return (
      <Flx ac jc sx={{ py: 6 }}>
        <Txt body2 color="text.secondary" sx={{ fontStyle: "italic" }}>
          No webhook events recorded yet.
        </Txt>
      </Flx>
    );

  return (
    <Flx column>
      {events.map((evt, i) => {
        const evtConfig = EVENT_ICON_MAP[evt.event_type] || {
          icon: "📌",
          color: grey[100],
          textColor: grey[700],
        };
        const evtDisplay = REGGORA_EVENT_DISPLAY[evt.event_type] || {
          label: evt.event_type,
        };
        const timeStr =
          evt.stored_at || evt.event_time
            ? new Date(evt.stored_at || evt.event_time).toLocaleString()
            : "";
        return (
          <Box key={i} sx={{ py: 1, px: 1.5 }}>
            <Flx ac gap={1}>
              <Chip
                label={`${evtConfig.icon} ${evtDisplay.label}`}
                size="small"
                sx={{
                  backgroundColor: evtConfig.color,
                  color: evtConfig.textColor,
                  fontWeight: 600,
                  fontSize: "11px",
                }}
              />
              <Txt
                caption
                color="text.secondary"
                sx={{ fontFamily: "monospace", fontSize: "11px" }}
              >
                {evt.reggora_order_id || "—"}
              </Txt>
              <Txt caption color="text.secondary" sx={{ ml: "auto" }}>
                {timeStr}
              </Txt>
            </Flx>
            {evt.event_data && (
              <>
                <Txt
                  caption
                  color="text.secondary"
                  sx={{
                    mt: 0.5,
                    cursor: "pointer",
                    "&:hover": { color: "text.primary" },
                  }}
                  onClick={() => setExpandedIdx(expandedIdx === i ? null : i)}
                >
                  {expandedIdx === i ? "▼" : "▶"} Raw payload
                </Txt>
                <Collapse in={expandedIdx === i}>
                  <Box
                    component="pre"
                    sx={{
                      mt: 0.5,
                      p: 1,
                      backgroundColor: grey[50],
                      borderRadius: 1,
                      fontSize: "11px",
                      fontFamily: "monospace",
                      color: grey[600],
                      maxHeight: 150,
                      overflowY: "auto",
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                    }}
                  >
                    {JSON.stringify(evt.event_data, null, 2)}
                  </Box>
                </Collapse>
              </>
            )}
            {i < events.length - 1 && <Divider sx={{ mt: 1 }} />}
          </Box>
        );
      })}
    </Flx>
  );
};

// ═══════════════════════════════════════════════════════════
// Right Column: Comments + History + Contacts
// ═══════════════════════════════════════════════════════════

const CommentsHistoryColumn = ({
  notifications,
  webhookEvents,
  contacts,
  onRefresh,
  loading,
}) => {
  const [activeTab, setActiveTab] = useState("comments");
  return (
    <Paper
      elevation={0}
      sx={{
        border: `1px solid ${grey[200]}`,
        borderRadius: "8px",
        overflow: "hidden",
        position: "sticky",
        top: 16,
      }}
    >
      <Box
        sx={{
          borderBottom: `1px solid ${grey[200]}`,
          px: 1.5,
          py: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Flx ac gap={0.5}>
          <Button
            size="small"
            variant={activeTab === "comments" ? "contained" : "text"}
            color={activeTab === "comments" ? "primary" : "inherit"}
            startIcon={
              <ChatBubbleOutline sx={{ fontSize: "14px !important" }} />
            }
            onClick={() => setActiveTab("comments")}
            sx={{
              textTransform: "none",
              fontSize: "12px",
              minHeight: 30,
              px: 1.5,
              ...(activeTab !== "comments" && { color: grey[600] }),
            }}
          >
            Comments
            {notifications.length > 0 && (
              <Chip
                label={notifications.length}
                size="small"
                sx={{
                  ml: 0.5,
                  height: 18,
                  fontSize: "10px",
                  fontWeight: 700,
                  ...(activeTab === "comments"
                    ? {
                        backgroundColor: "rgba(255,255,255,0.3)",
                        color: "inherit",
                      }
                    : { backgroundColor: blue[600], color: "#fff" }),
                }}
              />
            )}
          </Button>
          <Button
            size="small"
            variant={activeTab === "history" ? "contained" : "text"}
            color={activeTab === "history" ? "primary" : "inherit"}
            startIcon={<HistoryOutlined sx={{ fontSize: "14px !important" }} />}
            onClick={() => setActiveTab("history")}
            sx={{
              textTransform: "none",
              fontSize: "12px",
              minHeight: 30,
              px: 1.5,
              ...(activeTab !== "history" && { color: grey[600] }),
            }}
          >
            Webhooks
            {webhookEvents.length > 0 && (
              <Chip
                label={webhookEvents.length}
                size="small"
                sx={{
                  ml: 0.5,
                  height: 18,
                  fontSize: "10px",
                  fontWeight: 700,
                  backgroundColor: grey[500],
                  color: "#fff",
                }}
              />
            )}
          </Button>
        </Flx>
        <IconButton
          size="small"
          onClick={onRefresh}
          disabled={loading}
          sx={{ color: grey[400] }}
        >
          {loading ? (
            <CircularProgress size={14} />
          ) : (
            <RefreshOutlined sx={{ fontSize: 16 }} />
          )}
        </IconButton>
      </Box>
      <Box sx={{ maxHeight: 600, overflowY: "auto" }}>
        {activeTab === "comments" && (
          <CommentsPanel notifications={notifications} contacts={contacts} />
        )}
        {activeTab === "history" && (
          <WebhookHistoryPanel events={webhookEvents} />
        )}
      </Box>
      {contacts?.length > 0 && (
        <Box sx={{ borderTop: `1px solid ${grey[200]}`, px: 2, py: 1.5 }}>
          <Flx ac gap={0.5} sx={{ mb: 1 }}>
            <PeopleOutline sx={{ fontSize: 14, color: grey[500] }} />
            <Txt caption color="text.secondary" bold>
              Deal Contacts ({contacts.length})
            </Txt>
          </Flx>
          <Flx sx={{ flexWrap: "wrap", gap: 0.5 }}>
            {contacts.map((c, ci) => {
              const name =
                [c.firstName, c.lastName].filter(Boolean).join(" ") ||
                c.emailAddress ||
                "?";
              const role = c.contact_type || c.role || "";
              const typeColors = CONTACT_TYPE_COLORS[role.toLowerCase()] || {
                bg: grey[100],
                color: grey[600],
                border: grey[200],
              };
              return (
                <Chip
                  key={ci}
                  size="small"
                  label={
                    <>
                      <span style={{ fontWeight: 600 }}>{name}</span>
                      {role && (
                        <span style={{ opacity: 0.7, marginLeft: 4 }}>
                          ({role})
                        </span>
                      )}
                    </>
                  }
                  sx={{
                    fontSize: "11px",
                    height: 24,
                    backgroundColor: typeColors.bg,
                    color: typeColors.color,
                    border: `1px solid ${typeColors.border}`,
                  }}
                />
              );
            })}
          </Flx>
        </Box>
      )}
    </Paper>
  );
};

// ═══════════════════════════════════════════════════════════
// MAIN SCREEN
// ═══════════════════════════════════════════════════════════

const ReggoraAppraisalOrderScreen = () => {
  const user = useSelector((state) => state.user);
  const userEmail = user?.emailAddress || "";

  const {
    loading,
    sseProgress,
    loanLookup,
    checkExistingLoans,
    createLoans,
    fetchProducts,
    submitOrders,
    fetchOrders,
    fetchOrderDetails,
    fetchDealDocuments,
    uploadEvaultDocs,
    holdOrder,
    fetchConversation,
    sendConversationMessage,
    fetchLoanDiff,
    simulateWebhook,
  } = useReggoraRouteHook();

  // ── Flow state ──
  const [reggStep, setReggStep] = useState("lookup");

  // ── Lookup state ──
  const [loanNumber, setLoanNumber] = useState("");
  const [lookupResult, setLookupResult] = useState(null);
  const [lookupError, setLookupError] = useState(null);
  const [dealId, setDealId] = useState("");
  const [dealContacts, setDealContacts] = useState([]);
  const [existingLoans, setExistingLoans] = useState([]);
  const [existingOrders, setExistingOrders] = useState([]);
  const [existingLoaded, setExistingLoaded] = useState(false);

  // ── Order config ──
  const [dueDate, setDueDate] = useState("");
  const [priority, setPriority] = useState("Normal");
  const [allocationType, setAllocationType] = useState("automatically");
  const [reggProducts, setReggProducts] = useState([]);
  const [selectedProductIds, setSelectedProductIds] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);

  // ── Loans editing ──
  const [editingLoans, setEditingLoans] = useState([]);
  const [activePropertyTab, setActivePropertyTab] = useState(0);

  // ── SSE progress ──
  const [progressEvents, setProgressEvents] = useState([]);
  const [currentProgress, setCurrentProgress] = useState({
    percent: 0,
    message: "",
  });
  const [errorMsg, setErrorMsg] = useState(null);

  // ── Post-order state ──
  const [reggOrders, setReggOrders] = useState([]);
  const [webhookEvents, setWebhookEvents] = useState([]);
  const [notificationsTab, setNotificationsTab] = useState("comments");

  // ── Webhook simulator ──
  const [webhookOrderId, setWebhookOrderId] = useState("");
  const [webhookEventType, setWebhookEventType] = useState("");
  const [webhookStatus, setWebhookStatus] = useState("");
  const [webhookPaymentAmount, setWebhookPaymentAmount] = useState("");
  const [webhookInspectionDate, setWebhookInspectionDate] = useState("");
  const [webhookSubmissionVersion, setWebhookSubmissionVersion] = useState("1");
  const [webhookResponse, setWebhookResponse] = useState(null);

  // ── eVault ──
  const [dealDocuments, setDealDocuments] = useState([]);
  const [dealBlobFiles, setDealBlobFiles] = useState([]);
  const [selectedDocKeys, setSelectedDocKeys] = useState([]);
  const [evaultResult, setEvaultResult] = useState(null);
  const [evaultUploading, setEvaultUploading] = useState(false);

  // ── Hold ──
  const [holdOrderId, setHoldOrderId] = useState(null);
  const [holdReason, setHoldReason] = useState("");

  // ── Conversation ──
  const [msgOrderId, setMsgOrderId] = useState(null);
  const [msgText, setMsgText] = useState("");
  const [msgConversation, setMsgConversation] = useState(null);
  const [msgConvLoading, setMsgConvLoading] = useState(false);

  // ── Diff ──
  const [diffData, setDiffData] = useState(null);

  // ── Fetch products on mount ──
  useEffect(() => {
    setProductsLoading(true);
    fetchProducts({
      onSuccessFn: (data) => {
        setReggProducts(data?.products || []);
        setProductsLoading(false);
      },
      onFailFn: () => setProductsLoading(false),
    });
  }, []); // eslint-disable-line

  // ── Aggregate notifications ──
  const allNotifications = useMemo(() => {
    return reggOrders.flatMap((o) =>
      (o.notifications || []).map((n) => ({
        ...n,
        order_id: o.external_order_id || "",
      })),
    );
  }, [reggOrders]);

  // ═══════════════════════════════════════════════════════
  // Handlers
  // ═══════════════════════════════════════════════════════

  const handleLookup = useCallback(() => {
    if (isNil(loanNumber)) return;
    setLookupResult(null);
    setLookupError(null);
    setExistingLoans([]);
    setExistingOrders([]);
    setExistingLoaded(false);

    loanLookup({
      loanIdentifier: loanNumber,
      onSuccessFn: (data) => {
        if (data?.found === false) {
          setLookupError("No loan found with that number");
          return;
        }
        setLookupResult(data);
        setDealId(data.deal_id);
        setDealContacts(data.loanRelatedContacts || []);

        checkExistingLoans({
          dealId: data.deal_id,
          onSuccessFn: (ex) => {
            setExistingLoans(ex.loans || []);
            setExistingOrders(ex.orders || []);
            setExistingLoaded(true);
          },
          onFailFn: () => setExistingLoaded(true),
        });
      },
      onFailFn: (err) => setLookupError(err?.message || "Lookup failed"),
    });
  }, [loanNumber, loanLookup, checkExistingLoans]);

  const handleCreateLoans = useCallback(() => {
    if (!dealId) return;
    setReggStep("creating");
    setErrorMsg(null);

    createLoans({
      dealId,
      userEmail,
      onSuccessFn: (data) => {
        setEditingLoans(JSON.parse(JSON.stringify(data?.loans || [])));
        setActivePropertyTab(0);
        setReggStep("editing");

        // Load deal documents for eVault
        fetchDealDocuments({
          dealId,
          onSuccessFn: (docData) => {
            setDealDocuments(docData?.documents || []);
            setDealBlobFiles(docData?.blobFiles || []);
          },
        });
      },
      onFailFn: (err) => {
        setErrorMsg(err?.message || "Create loans failed");
        setReggStep("error");
      },
    });
  }, [dealId, userEmail, createLoans, fetchDealDocuments]);

  const handleSubmitOrders = useCallback(() => {
    if (!dealId || editingLoans.length === 0) return;
    setReggStep("submitting");
    setProgressEvents([]);
    setCurrentProgress({ percent: 0, message: "Submitting orders..." });
    setErrorMsg(null);

    submitOrders({
      dealId,
      loans: editingLoans.map((l) => ({
        reggora_loan_id: l.reggora_loan_id,
        property_index: l.property_index,
        loan_payload: l.loan_payload,
        primary_borrower: l.primary_borrower,
        related_contacts: l.related_contacts || [],
      })),
      selectedProducts: selectedProductIds,
      dueDate: dueDate || undefined,
      priority,
      allocationType,
      userEmail,
      onProgressFn: (progress) => {
        setProgressEvents((prev) => [...prev, progress]);
        setCurrentProgress({
          percent: progress.percent,
          message: progress.message,
        });
      },
      onProcessCompleteFn: () => {
        setReggStep("complete");
        // Auto-fetch orders after short delay
        setTimeout(() => handleFetchOrders(), 1000);
      },
      onSuccessFn: () => {
        setReggStep("complete");
      },
      onFailFn: (err) => {
        setErrorMsg(err?.message || "Order submission failed");
        setReggStep("error");
      },
    });
  }, [
    dealId,
    editingLoans,
    selectedProductIds,
    dueDate,
    priority,
    allocationType,
    userEmail,
    submitOrders,
  ]);

  const handleFetchOrders = useCallback(() => {
    if (!dealId) return;
    fetchOrders({
      dealId,
      onSuccessFn: (data) => {
        setReggOrders(data?.orders || []);
        setWebhookEvents(data?.webhook_events || []);
        if (data?.loanRelatedContacts)
          setDealContacts(data.loanRelatedContacts);
        const orders = data?.orders || [];
        if (orders.length > 0 && !webhookOrderId) {
          setWebhookOrderId(orders[0].external_order_id || "");
        }
      },
    });
    fetchLoanDiff({
      dealId,
      onSuccessFn: (data) => setDiffData(data),
    });
  }, [dealId, webhookOrderId, fetchOrders, fetchLoanDiff]);

  const handleSimulateWebhook = useCallback(
    (eventType, overrides = {}) => {
      if (!webhookOrderId) return;

      const extra = {};
      const evtType = eventType || webhookEventType;
      if (PAYMENT_EVENT_TYPES.has(evtType)) {
        if (webhookPaymentAmount) extra.amount = webhookPaymentAmount;
        extra.payment_id = `sim-pay-${Date.now()}`;
      }
      if (
        evtType === "status_update" &&
        (overrides.status || webhookStatus) === "Inspection Scheduled" &&
        webhookInspectionDate
      ) {
        extra.inspection_date = webhookInspectionDate;
      }
      if (
        (evtType === "order_report_received" ||
          evtType === "order_ready_for_review") &&
        webhookSubmissionVersion
      ) {
        extra.submission_version = webhookSubmissionVersion;
      }

      simulateWebhook({
        reggoraOrderId: webhookOrderId,
        eventType: evtType,
        status: overrides.status || webhookStatus || undefined,
        eventData: Object.keys(extra).length > 0 ? extra : undefined,
        onSuccessFn: (data) => {
          setWebhookResponse(data);
          setTimeout(() => handleFetchOrders(), 500);
        },
        onFailFn: (err) => setWebhookResponse({ error: err?.message }),
      });
    },
    [
      webhookOrderId,
      webhookEventType,
      webhookStatus,
      webhookPaymentAmount,
      webhookInspectionDate,
      webhookSubmissionVersion,
      simulateWebhook,
      handleFetchOrders,
    ],
  );

  const handleHoldOrder = useCallback(
    (orderId) => {
      holdOrder({
        reggoraOrderId: orderId,
        reason: holdReason,
        onSuccessFn: () => {
          setHoldOrderId(null);
          setHoldReason("");
          handleFetchOrders();
        },
      });
    },
    [holdReason, holdOrder, handleFetchOrders],
  );

  const handleSendMessage = useCallback(
    (orderId) => {
      if (!msgText.trim()) return;
      sendConversationMessage({
        orderId,
        message: msgText.trim(),
        onSuccessFn: () => {
          setMsgText("");
          // Refresh conversation
          fetchConversation({
            orderId,
            onSuccessFn: (data) => setMsgConversation(data),
          });
          handleFetchOrders();
        },
      });
    },
    [msgText, sendConversationMessage, fetchConversation, handleFetchOrders],
  );

  const handleEvaultUpload = useCallback(() => {
    if (selectedDocKeys.length === 0 || reggOrders.length === 0) return;
    const orderId = reggOrders[0]?.external_order_id;
    if (!orderId) return;
    setEvaultUploading(true);
    uploadEvaultDocs({
      reggoraOrderId: orderId,
      dealId,
      docKeys: selectedDocKeys,
      onSuccessFn: (result) => {
        setEvaultResult(result);
        setEvaultUploading(false);
      },
      onFailFn: (err) => {
        setEvaultResult({ error: err?.message });
        setEvaultUploading(false);
      },
    });
  }, [selectedDocKeys, reggOrders, dealId, uploadEvaultDocs]);

  // ── Loan editing helpers ──
  const updateLoanPayloadField = (idx, field, value) => {
    setEditingLoans((prev) => {
      const updated = [...prev];
      updated[idx] = {
        ...updated[idx],
        loan_payload: { ...updated[idx].loan_payload, [field]: value },
      };
      return updated;
    });
  };
  const updatePrimaryBorrower = (idx, field, value) => {
    setEditingLoans((prev) => {
      const updated = [...prev];
      updated[idx] = {
        ...updated[idx],
        primary_borrower: { ...updated[idx].primary_borrower, [field]: value },
      };
      return updated;
    });
  };

  const reggReset = useCallback(() => {
    setReggStep("lookup");
    setLookupResult(null);
    setLookupError(null);
    setDealId("");
    setDealContacts([]);
    setExistingLoans([]);
    setExistingOrders([]);
    setExistingLoaded(false);
    setEditingLoans([]);
    setActivePropertyTab(0);
    setProgressEvents([]);
    setCurrentProgress({ percent: 0, message: "" });
    setErrorMsg(null);
    setReggOrders([]);
    setWebhookEvents([]);
    setWebhookOrderId("");
    setWebhookEventType("");
    setWebhookStatus("");
    setWebhookResponse(null);
    setDealDocuments([]);
    setDealBlobFiles([]);
    setSelectedDocKeys([]);
    setEvaultResult(null);
    setHoldOrderId(null);
    setHoldReason("");
    setMsgOrderId(null);
    setMsgText("");
    setMsgConversation(null);
    setDiffData(null);
  }, []);

  // ═══════════════════════════════════════════════════════
  // Render
  // ═══════════════════════════════════════════════════════

  return (
    <Box sx={{ background: "#f5f8fa", minHeight: "100vh", pb: 6 }}>
      {/* Header */}
      <Box
        sx={{
          background: "#ffffff",
          pt: 3,
          pb: 3,
          boxShadow: "0 0 0px 1px #dde0e4",
        }}
      >
        <ScreenContent suppressPaddingTopBottom>
          <Flx fw jb ac>
            <Flx ac gap={1}>
              <DescriptionOutlined className="thin" />
              <Htag h1>Order Appraisal</Htag>
            </Flx>
            {reggStep !== "lookup" && (
              <Button size="small" variant="text" onClick={reggReset}>
                Reset
              </Button>
            )}
          </Flx>
        </ScreenContent>
      </Box>

      <Box
        sx={{
          maxWidth:
            reggStep === "complete" || reggStep === "streaming"
              ? "1400px"
              : "900px",
          mx: "auto",
          p: 3,
        }}
      >
        <Flx column gap={2.5}>
          {/* Step indicator */}
          <StepIndicator currentStep={reggStep} />

          {/* Error banner */}
          {reggStep === "error" && errorMsg && (
            <Alert severity="error">
              <Txt bold body2>
                Error
              </Txt>
              <Txt body2 sx={{ mt: 0.5 }}>
                {errorMsg}
              </Txt>
            </Alert>
          )}

          {/* ═════════════════════════════════════════════════ */}
          {/* STEP 1: LOAN LOOKUP                               */}
          {/* ═════════════════════════════════════════════════ */}
          {(reggStep === "lookup" || reggStep === "error") && (
            <SectionPaper title="Step 1: Loan Lookup">
              <Flx ac gap={1.5} sx={{ mb: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <InputWrapper label="Search by Loan Number">
                    <Flx ac fw gap={1}>
                      <LoanSearchDropdown
                        value={loanNumber}
                        onChange={setLoanNumber}
                      />
                      <Button
                        variant="contained"
                        size="large"
                        startIcon={
                          loading ? (
                            <CircularProgress size={16} />
                          ) : (
                            <SearchOutlined />
                          )
                        }
                        onClick={handleLookup}
                        disabled={loading || !loanNumber}
                      >
                        Look Up
                      </Button>
                    </Flx>
                  </InputWrapper>
                </Box>
              </Flx>

              {lookupError && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  {lookupError}
                </Alert>
              )}

              {/* Lookup result */}
              {lookupResult && (
                <Flx column gap={2}>
                  <Alert severity="success" icon={<CheckCircleOutline />}>
                    <Flx column gap={0.5}>
                      <Txt body2 bold>
                        Loan found
                      </Txt>
                      <Flx gap={3} sx={{ flexWrap: "wrap" }}>
                        <Txt caption>
                          <Txt caption color="text.secondary" component="span">
                            Deal ID:
                          </Txt>{" "}
                          <span style={{ fontFamily: "monospace" }}>
                            {lookupResult.deal_id}
                          </span>
                        </Txt>
                        <Txt caption>
                          <Txt caption color="text.secondary" component="span">
                            LQ:
                          </Txt>{" "}
                          {lookupResult.sync_loan_id || "—"}
                        </Txt>
                        <Txt caption>
                          <Txt caption color="text.secondary" component="span">
                            Loan #:
                          </Txt>{" "}
                          {lookupResult.internal_loan_number || "—"}
                        </Txt>
                        <Txt caption>
                          <Txt caption color="text.secondary" component="span">
                            Product:
                          </Txt>{" "}
                          {lookupResult.loan_product_type || "—"}
                        </Txt>
                        <Txt caption>
                          <Txt caption color="text.secondary" component="span">
                            Purpose:
                          </Txt>{" "}
                          {lookupResult.loan_purpose || "—"}
                        </Txt>
                        <Txt caption>
                          <Txt caption color="text.secondary" component="span">
                            Borrowers:
                          </Txt>{" "}
                          {lookupResult.borrower_names?.join(", ") || "—"}
                        </Txt>
                        {lookupResult.borrower_entity && (
                          <Txt caption>
                            <Txt
                              caption
                              color="text.secondary"
                              component="span"
                            >
                              Entity:
                            </Txt>{" "}
                            {lookupResult.borrower_entity}
                          </Txt>
                        )}
                        {lookupResult.salesperson_email && (
                          <Txt caption>
                            <Txt
                              caption
                              color="text.secondary"
                              component="span"
                            >
                              Salesperson:
                            </Txt>{" "}
                            {lookupResult.salesperson_email}
                          </Txt>
                        )}
                      </Flx>
                    </Flx>
                  </Alert>

                  {/* Existing Reggora records warning */}
                  {existingLoaded &&
                    (existingLoans.length > 0 || existingOrders.length > 0) && (
                      <Alert
                        severity="warning"
                        icon={<WarningAmberOutlined />}
                        sx={{ border: `1px solid ${amber[200]}` }}
                      >
                        <Flx column gap={1}>
                          <Txt bold body2>
                            Existing Reggora Records Found
                          </Txt>
                          <Txt caption color="text.secondary">
                            This deal already has Reggora records. Creating new
                            loans may fail if they already exist.
                          </Txt>
                          {existingLoans.length > 0 && (
                            <Flx column gap={0.5}>
                              <Txt caption bold>
                                ExternalLoan Records ({existingLoans.length})
                              </Txt>
                              {existingLoans.map((el, i) => (
                                <Box
                                  key={i}
                                  sx={{
                                    p: 1,
                                    backgroundColor: "#fff",
                                    border: `1px solid ${amber[200]}`,
                                    borderRadius: 1,
                                  }}
                                >
                                  <Txt caption sx={{ fontFamily: "monospace" }}>
                                    {el.external_loan_id || "—"}
                                  </Txt>
                                  {el.created_date && (
                                    <Txt
                                      caption
                                      color="text.secondary"
                                      sx={{ ml: 1 }}
                                    >
                                      Created:{" "}
                                      {new Date(
                                        el.created_date,
                                      ).toLocaleString()}
                                    </Txt>
                                  )}
                                </Box>
                              ))}
                            </Flx>
                          )}
                          {existingOrders.length > 0 && (
                            <Flx column gap={0.5}>
                              <Txt caption bold>
                                Orders ({existingOrders.length})
                              </Txt>
                              {existingOrders.map((o, i) => (
                                <Flx
                                  key={i}
                                  ac
                                  jb
                                  sx={{
                                    p: 1,
                                    backgroundColor: "#fff",
                                    border: `1px solid ${amber[200]}`,
                                    borderRadius: 1,
                                  }}
                                >
                                  <Txt caption sx={{ fontFamily: "monospace" }}>
                                    {o.external_order_id || "—"}
                                  </Txt>
                                  <StatusChip
                                    status={
                                      o.status || o.order_status || "unknown"
                                    }
                                  />
                                </Flx>
                              ))}
                            </Flx>
                          )}
                          {existingLoans.length > 0 && (
                            <Button
                              variant="contained"
                              color="warning"
                              size="small"
                              onClick={() => {
                                const loans = existingLoans.map((el, ix) => ({
                                  property_index: ix,
                                  reggora_loan_id: el.external_loan_id || "",
                                  loan_payload: el.external_loan_data || {},
                                  primary_borrower:
                                    lookupResult?.primary_borrower || {},
                                  related_contacts: [],
                                }));
                                setEditingLoans(
                                  JSON.parse(JSON.stringify(loans)),
                                );
                                setActivePropertyTab(0);
                                setReggStep("editing");
                              }}
                            >
                              Use Existing Loans — Skip to Edit & Submit
                            </Button>
                          )}
                        </Flx>
                      </Alert>
                    )}

                  {/* Order config */}
                  <Divider />
                  <Flx gap={2} sx={{ flexWrap: "wrap" }}>
                    <MuiTextField
                      size="small"
                      type="date"
                      label="Due Date"
                      InputLabelProps={{ shrink: true }}
                      value={dueDate}
                      onChange={(e) => setDueDate(e.target.value)}
                    />
                    <MuiTextField
                      size="small"
                      select
                      label="Priority"
                      value={priority}
                      onChange={(e) => setPriority(e.target.value)}
                      SelectProps={{ native: true }}
                      sx={{ minWidth: 120 }}
                    >
                      {REGGORA_PRIORITIES.map((p) => (
                        <option key={p} value={p}>
                          {p}
                        </option>
                      ))}
                    </MuiTextField>
                    <MuiTextField
                      size="small"
                      select
                      label="Allocation"
                      value={allocationType}
                      onChange={(e) => setAllocationType(e.target.value)}
                      SelectProps={{ native: true }}
                      sx={{ minWidth: 140 }}
                    >
                      {REGGORA_ALLOCATION_TYPES.map((a) => (
                        <option key={a} value={a}>
                          {a}
                        </option>
                      ))}
                    </MuiTextField>
                  </Flx>

                  {/* Products */}
                  <Flx column gap={1}>
                    <Txt body2 bold>
                      Appraisal Products
                    </Txt>
                    {productsLoading && <CircularProgress size={16} />}
                    <SelectMultipleCheckboxInput
                      disable
                      value={selectedProductIds}
                      onChange={setSelectedProductIds}
                      options={reggProducts?.map((rp) => {
                        return {
                          label: rp.name,
                          value: rp.id,
                        };
                      })}
                    />

                    {selectedProductIds.length > 0 && (
                      <Txt caption color="text.secondary">
                        {selectedProductIds.length} product(s) selected
                      </Txt>
                    )}
                  </Flx>

                  <Button
                    variant="contained"
                    onClick={handleCreateLoans}
                    disabled={!dealId || loading}
                  >
                    {loading ? "Creating Loans..." : "Create Reggora Loans"}
                  </Button>
                </Flx>
              )}
            </SectionPaper>
          )}

          {/* ═════════════════════════════════════════════════ */}
          {/* STEP 2: CREATING (loading)                        */}
          {/* ═════════════════════════════════════════════════ */}
          {reggStep === "creating" && (
            <SectionPaper title="Creating Reggora Loans...">
              <Flx ac gap={1.5}>
                <CircularProgress size={20} />
                <Txt body2 color="text.secondary">
                  Creating loans in Reggora...
                </Txt>
              </Flx>
            </SectionPaper>
          )}

          {/* ═════════════════════════════════════════════════ */}
          {/* STEP 3: EDIT LOANS                                */}
          {/* ═════════════════════════════════════════════════ */}
          {reggStep === "editing" && editingLoans.length > 0 && (
            <SectionPaper title="Step 3: Edit Loan Data">
              {/* Deal summary banner */}
              <Alert
                severity="info"
                sx={{ mb: 2, border: `1px solid ${blue[200]}` }}
              >
                <Flx gap={2} sx={{ flexWrap: "wrap" }}>
                  <Txt caption>
                    <b>Deal:</b>{" "}
                    <span style={{ fontFamily: "monospace" }}>{dealId}</span>
                  </Txt>
                  {lookupResult?.sync_loan_id && (
                    <Txt caption>
                      <b>LQ:</b> {lookupResult.sync_loan_id}
                    </Txt>
                  )}
                  <Txt caption>
                    <b>Loans:</b> {editingLoans.length}
                  </Txt>
                </Flx>
              </Alert>

              {/* Property tabs */}
              {editingLoans.length > 1 && (
                <Tabs
                  value={activePropertyTab}
                  onChange={(_, v) => setActivePropertyTab(v)}
                  sx={{ mb: 2 }}
                >
                  {editingLoans.map((l, i) => {
                    const addr =
                      l.loan_payload?.["Subject Property Street Address"] ||
                      l.loan_payload?.subject_property?.address ||
                      `Property ${i + 1}`;
                    return <Tab key={i} label={addr} />;
                  })}
                </Tabs>
              )}

              {(() => {
                const loan = editingLoans[activePropertyTab];
                if (!loan) return null;
                const lp = loan.loan_payload || {};
                const pb = loan.primary_borrower || {};
                const idx = activePropertyTab;

                return (
                  <Flx column gap={3}>
                    {/* Reggora loan ID */}
                    <Chip
                      label={`reggora_loan_id: ${loan.reggora_loan_id}`}
                      size="small"
                      sx={{
                        alignSelf: "flex-start",
                        fontFamily: "monospace",
                        backgroundColor: indigo[50],
                        color: indigo[700],
                      }}
                    />

                    {/* Property Address */}
                    <Flx column gap={1.5}>
                      <Txt body2 bold>
                        Property Address
                      </Txt>
                      <Flx gap={2} sx={{ flexWrap: "wrap" }}>
                        <MuiTextField
                          size="small"
                          label="Street Address"
                          value={
                            lp["Subject Property Street Address"] ||
                            lp.subject_property?.address ||
                            ""
                          }
                          onChange={(e) =>
                            updateLoanPayloadField(
                              idx,
                              "Subject Property Street Address",
                              e.target.value,
                            )
                          }
                          sx={{ minWidth: 250 }}
                        />
                        <MuiTextField
                          size="small"
                          label="City"
                          value={
                            lp["Subject Property City"] ||
                            lp.subject_property?.city ||
                            ""
                          }
                          onChange={(e) =>
                            updateLoanPayloadField(
                              idx,
                              "Subject Property City",
                              e.target.value,
                            )
                          }
                        />
                        <MuiTextField
                          size="small"
                          label="State"
                          value={
                            lp["Subject Property State"] ||
                            lp.subject_property?.state ||
                            ""
                          }
                          onChange={(e) =>
                            updateLoanPayloadField(
                              idx,
                              "Subject Property State",
                              e.target.value,
                            )
                          }
                          sx={{ width: 80 }}
                        />
                        <MuiTextField
                          size="small"
                          label="ZIP"
                          value={
                            lp["Subject Property ZIP"] ||
                            lp.subject_property?.zip ||
                            ""
                          }
                          onChange={(e) =>
                            updateLoanPayloadField(
                              idx,
                              "Subject Property ZIP",
                              e.target.value,
                            )
                          }
                          sx={{ width: 100 }}
                        />
                      </Flx>
                    </Flx>

                    {/* Primary Borrower Selection */}
                    <Flx column gap={1.5}>
                      <Txt body2 bold>
                        Primary Contact for Appraisal
                      </Txt>
                      {lookupResult?.borrowers?.length > 0 && (
                        <Flx column gap={1}>
                          {lookupResult.borrowers.map((b, bi) => {
                            const bName =
                              [b.firstName, b.lastName]
                                .filter(Boolean)
                                .join(" ") || "(unnamed)";
                            const isSelected =
                              pb.firstName === b.firstName &&
                              pb.lastName === b.lastName;
                            return (
                              <Paper
                                key={bi}
                                variant="outlined"
                                onClick={() => {
                                  updatePrimaryBorrower(
                                    idx,
                                    "firstName",
                                    b.firstName || "",
                                  );
                                  updatePrimaryBorrower(
                                    idx,
                                    "lastName",
                                    b.lastName || "",
                                  );
                                  updatePrimaryBorrower(
                                    idx,
                                    "emailAddress",
                                    b.emailAddress || "",
                                  );
                                  updatePrimaryBorrower(
                                    idx,
                                    "phone",
                                    b.phone || "",
                                  );
                                }}
                                sx={{
                                  p: 1.5,
                                  cursor: "pointer",
                                  borderColor: isSelected
                                    ? blue[500]
                                    : grey[300],
                                  backgroundColor: isSelected
                                    ? blue[50]
                                    : "#fff",
                                  "&:hover": {
                                    borderColor: isSelected
                                      ? blue[500]
                                      : grey[400],
                                  },
                                  transition: "all 0.15s",
                                }}
                              >
                                <Flx ac gap={1.5}>
                                  <Radio size="small" checked={isSelected} />
                                  <Flx column sx={{ flex: 1 }}>
                                    <Flx ac gap={1}>
                                      <Txt body2 bold>
                                        {bName}
                                      </Txt>
                                      {b.isGuarantor && (
                                        <Chip
                                          label="Guarantor"
                                          size="small"
                                          sx={{
                                            height: 20,
                                            fontSize: "10px",
                                            backgroundColor: indigo[50],
                                            color: indigo[700],
                                          }}
                                        />
                                      )}
                                      {isSelected && (
                                        <Chip
                                          label="Primary Contact"
                                          size="small"
                                          sx={{
                                            height: 20,
                                            fontSize: "10px",
                                            backgroundColor: blue[600],
                                            color: "#fff",
                                          }}
                                        />
                                      )}
                                    </Flx>
                                    <Flx ac gap={2}>
                                      {b.emailAddress && (
                                        <Txt caption color="text.secondary">
                                          {b.emailAddress}
                                        </Txt>
                                      )}
                                      {b.phone && (
                                        <Txt caption color="text.secondary">
                                          {b.phone}
                                        </Txt>
                                      )}
                                      {b.entityPercentOwnership > 0 && (
                                        <Txt caption color="text.secondary">
                                          {Math.round(
                                            b.entityPercentOwnership * 100,
                                          )}
                                          % ownership
                                        </Txt>
                                      )}
                                    </Flx>
                                  </Flx>
                                </Flx>
                              </Paper>
                            );
                          })}
                        </Flx>
                      )}
                      <Flx gap={2} sx={{ flexWrap: "wrap" }}>
                        <MuiTextField
                          size="small"
                          label="First Name"
                          value={pb.firstName || ""}
                          onChange={(e) =>
                            updatePrimaryBorrower(
                              idx,
                              "firstName",
                              e.target.value,
                            )
                          }
                        />
                        <MuiTextField
                          size="small"
                          label="Last Name"
                          value={pb.lastName || ""}
                          onChange={(e) =>
                            updatePrimaryBorrower(
                              idx,
                              "lastName",
                              e.target.value,
                            )
                          }
                        />
                        <MuiTextField
                          size="small"
                          label="Email"
                          value={pb.emailAddress || pb.email || ""}
                          onChange={(e) =>
                            updatePrimaryBorrower(
                              idx,
                              "emailAddress",
                              e.target.value,
                            )
                          }
                          sx={{ minWidth: 200 }}
                        />
                        <MuiTextField
                          size="small"
                          label="Phone"
                          value={pb.phone || ""}
                          onChange={(e) =>
                            updatePrimaryBorrower(idx, "phone", e.target.value)
                          }
                        />
                      </Flx>
                    </Flx>

                    {/* Read-only loan details */}
                    {(lp["Loan Number"] || lp["Loan Officer"]) && (
                      <Flx column gap={1}>
                        <Txt body2 bold>
                          Loan Details
                        </Txt>
                        <Flx gap={3}>
                          {lp["Loan Number"] && (
                            <Txt caption>
                              <b>Loan Number:</b>{" "}
                              <span style={{ fontFamily: "monospace" }}>
                                {lp["Loan Number"]}
                              </span>
                            </Txt>
                          )}
                          {lp["Loan Officer"] && (
                            <Txt caption>
                              <b>Loan Officer:</b>{" "}
                              <span style={{ fontFamily: "monospace" }}>
                                {lp["Loan Officer"]}
                              </span>
                            </Txt>
                          )}
                        </Flx>
                      </Flx>
                    )}

                    {/* Consumers */}
                    {lp.consumers?.length > 0 && (
                      <Flx column gap={1}>
                        <Txt body2 bold>
                          Consumers ({lp.consumers.length})
                        </Txt>
                        {lp.consumers.map((c, ci) => (
                          <Box
                            key={ci}
                            sx={{
                              p: 1,
                              backgroundColor: grey[50],
                              borderRadius: 1,
                              border: `1px solid ${grey[200]}`,
                            }}
                          >
                            <Flx gap={3} sx={{ flexWrap: "wrap" }}>
                              <Txt caption>
                                <b>Name:</b> {c.full_name || "—"}
                              </Txt>
                              <Txt caption>
                                <b>Role:</b> {c.role || "—"}
                              </Txt>
                              {c.email && (
                                <Txt caption>
                                  <b>Email:</b> {c.email}
                                </Txt>
                              )}
                            </Flx>
                          </Box>
                        ))}
                      </Flx>
                    )}

                    {/* Related contacts */}
                    <Flx column gap={1}>
                      <Txt body2 bold>
                        Related Contacts
                      </Txt>
                      {(dealContacts || []).length > 0 ? (
                        <Flx column gap={0.75}>
                          {dealContacts.map((c, ci) => {
                            const role = c.contact_type || c.role || "";
                            const typeColors = CONTACT_TYPE_COLORS[
                              role.toLowerCase()
                            ] || {
                              bg: grey[100],
                              color: grey[600],
                              border: grey[200],
                            };
                            return (
                              <Paper
                                key={ci}
                                variant="outlined"
                                sx={{ p: 1.5 }}
                              >
                                <Flx ac gap={1.5}>
                                  <Flx column sx={{ flex: 1 }}>
                                    <Flx ac gap={1}>
                                      {role && (
                                        <Chip
                                          label={role}
                                          size="small"
                                          sx={{
                                            height: 22,
                                            fontSize: "11px",
                                            fontWeight: 600,
                                            backgroundColor: typeColors.bg,
                                            color: typeColors.color,
                                            border: `1px solid ${typeColors.border}`,
                                          }}
                                        />
                                      )}
                                    </Flx>
                                    <Txt body2 bold sx={{ mt: 0.5 }}>
                                      {[c.firstName, c.lastName]
                                        .filter(Boolean)
                                        .join(" ") || "(no name)"}
                                      {c.company && (
                                        <Txt
                                          caption
                                          color="text.secondary"
                                          component="span"
                                        >
                                          {" "}
                                          — {c.company}
                                        </Txt>
                                      )}
                                    </Txt>
                                    <Flx ac gap={2}>
                                      {c.emailAddress && (
                                        <Txt caption color="text.secondary">
                                          {c.emailAddress}
                                        </Txt>
                                      )}
                                      {c.phone && (
                                        <Txt caption color="text.secondary">
                                          {c.phone}
                                        </Txt>
                                      )}
                                    </Flx>
                                  </Flx>
                                </Flx>
                              </Paper>
                            );
                          })}
                        </Flx>
                      ) : (
                        <Txt
                          caption
                          color="text.secondary"
                          sx={{ fontStyle: "italic" }}
                        >
                          No related contacts on this deal.
                        </Txt>
                      )}
                    </Flx>

                    {/* Selected products summary */}
                    {selectedProductIds.length > 0 && (
                      <Alert
                        severity="info"
                        sx={{ border: `1px solid ${blue[200]}` }}
                      >
                        <Txt body2 bold>
                          Selected Products ({selectedProductIds.length})
                        </Txt>
                        {selectedProductIds.map((id) => {
                          const p = reggProducts.find((rp) => rp.id === id);
                          return (
                            <Txt key={id} caption>
                              • {p ? p.name : id}
                            </Txt>
                          );
                        })}
                      </Alert>
                    )}

                    {/* eVault documents accordion */}
                    <Accordion title="Documents to Upload to eVault">
                      {dealDocuments.length === 0 &&
                      dealBlobFiles.length === 0 ? (
                        <Txt
                          caption
                          color="text.secondary"
                          sx={{ fontStyle: "italic", py: 1 }}
                        >
                          No documents found for this deal.
                        </Txt>
                      ) : (
                        <Flx column gap={0.5} sx={{ py: 1 }}>
                          {dealDocuments
                            .filter((d) => d.blobFileKey)
                            .map((doc, i) => (
                              <Flx
                                key={`doc-${i}`}
                                ac
                                gap={1}
                                sx={{
                                  cursor: "pointer",
                                  "&:hover": { backgroundColor: grey[50] },
                                  px: 1,
                                  py: 0.5,
                                  borderRadius: 1,
                                }}
                                onClick={() =>
                                  setSelectedDocKeys((prev) =>
                                    prev.includes(doc.blobFileKey)
                                      ? prev.filter(
                                          (k) => k !== doc.blobFileKey,
                                        )
                                      : [...prev, doc.blobFileKey],
                                  )
                                }
                              >
                                <Checkbox
                                  size="small"
                                  checked={selectedDocKeys.includes(
                                    doc.blobFileKey,
                                  )}
                                  sx={{ p: 0.25 }}
                                />
                                <Txt body2>
                                  {doc.originalFileName ||
                                    doc.docType ||
                                    doc.blobFileKey}
                                </Txt>
                                {doc.docGroup && (
                                  <Txt caption color="text.secondary">
                                    ({doc.docGroup})
                                  </Txt>
                                )}
                              </Flx>
                            ))}
                          {dealBlobFiles
                            .filter(
                              (bf) =>
                                !dealDocuments.some(
                                  (d) => d.blobFileKey === bf.name,
                                ),
                            )
                            .map((bf, i) => (
                              <Flx
                                key={`blob-${i}`}
                                ac
                                gap={1}
                                sx={{
                                  cursor: "pointer",
                                  "&:hover": { backgroundColor: grey[50] },
                                  px: 1,
                                  py: 0.5,
                                  borderRadius: 1,
                                }}
                                onClick={() =>
                                  setSelectedDocKeys((prev) =>
                                    prev.includes(bf.name)
                                      ? prev.filter((k) => k !== bf.name)
                                      : [...prev, bf.name],
                                  )
                                }
                              >
                                <Checkbox
                                  size="small"
                                  checked={selectedDocKeys.includes(bf.name)}
                                  sx={{ p: 0.25 }}
                                />
                                <Txt body2>{bf.name}</Txt>
                                {bf.size && (
                                  <Txt caption color="text.secondary">
                                    ({(bf.size / 1024).toFixed(1)} KB)
                                  </Txt>
                                )}
                              </Flx>
                            ))}
                          {selectedDocKeys.length > 0 && (
                            <Txt caption sx={{ color: blue[600], mt: 1 }}>
                              {selectedDocKeys.length} document(s) selected for
                              eVault upload after order creation
                            </Txt>
                          )}
                        </Flx>
                      )}
                    </Accordion>

                    {/* Submit */}
                    <Flx ac gap={2}>
                      <Button
                        variant="contained"
                        onClick={handleSubmitOrders}
                        disabled={editingLoans.length === 0}
                      >
                        Submit Appraisal Orders ({editingLoans.length}{" "}
                        {editingLoans.length === 1 ? "loan" : "loans"})
                      </Button>
                      <Button variant="outlined" onClick={reggReset}>
                        Cancel
                      </Button>
                    </Flx>
                  </Flx>
                );
              })()}
            </SectionPaper>
          )}

          {/* ═════════════════════════════════════════════════ */}
          {/* STEP 4+5: SSE PROGRESS                            */}
          {/* ═════════════════════════════════════════════════ */}
          {(reggStep === "submitting" ||
            reggStep === "streaming" ||
            reggStep === "complete") && (
            <SectionPaper title="Appraisal Order Progress">
              <Flx column gap={2}>
                <Flx ac jb>
                  <Txt body2 color="text.secondary">
                    {currentProgress.message}
                  </Txt>
                  <Txt body2 bold sx={{ color: blue[600] }}>
                    {currentProgress.percent}%
                  </Txt>
                </Flx>
                <LinearProgress
                  variant="determinate"
                  value={currentProgress.percent || 0}
                  sx={{
                    height: 10,
                    borderRadius: 5,
                    ...(reggStep === "complete" && {
                      "& .MuiLinearProgress-bar": {
                        backgroundColor: green[500],
                      },
                    }),
                  }}
                />

                {/* Step log */}
                <Box
                  sx={{
                    backgroundColor: grey[50],
                    borderRadius: 1,
                    p: 1.5,
                    maxHeight: 200,
                    overflowY: "auto",
                  }}
                >
                  {progressEvents.map((evt, i) => (
                    <Flx key={i} ac gap={1} sx={{ py: 0.25 }}>
                      <Txt
                        caption
                        sx={{
                          fontFamily: "monospace",
                          color: grey[400],
                          width: 30,
                          textAlign: "right",
                        }}
                      >
                        {evt.percent}%
                      </Txt>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          backgroundColor:
                            evt.step === "error"
                              ? red[500]
                              : evt.step === "complete"
                                ? green[500]
                                : blue[500],
                        }}
                      />
                      <Txt
                        caption
                        sx={{
                          color:
                            evt.step === "error"
                              ? red[600]
                              : evt.step === "complete"
                                ? green[600]
                                : grey[600],
                        }}
                      >
                        {evt.message}
                      </Txt>
                    </Flx>
                  ))}
                  {progressEvents.length === 0 && (
                    <Txt
                      caption
                      color="text.secondary"
                      sx={{ fontStyle: "italic" }}
                    >
                      Waiting for events...
                    </Txt>
                  )}
                </Box>

                {reggStep === "complete" && (
                  <Alert severity="success">
                    <Txt bold body2>
                      Appraisal Orders Complete
                    </Txt>
                    {selectedDocKeys.length > 0 && (
                      <Box sx={{ mt: 1.5 }}>
                        <Txt body2>
                          {selectedDocKeys.length} document(s) selected for
                          eVault upload
                        </Txt>
                        {!evaultResult ? (
                          <Button
                            size="small"
                            variant="contained"
                            sx={{ mt: 1 }}
                            disabled={evaultUploading}
                            startIcon={<CloudUploadOutlined />}
                            onClick={handleEvaultUpload}
                          >
                            {evaultUploading
                              ? "Uploading..."
                              : "Upload to eVault"}
                          </Button>
                        ) : (
                          <Box sx={{ mt: 1 }}>
                            {evaultResult.error ? (
                              <Txt caption color="error.main">
                                Error: {evaultResult.error}
                              </Txt>
                            ) : (
                              <Txt caption color="success.main">
                                {evaultResult.uploaded?.length || 0} uploaded,{" "}
                                {evaultResult.errors?.length || 0} errors
                              </Txt>
                            )}
                          </Box>
                        )}
                      </Box>
                    )}
                  </Alert>
                )}
              </Flx>
            </SectionPaper>
          )}

          {/* ═════════════════════════════════════════════════ */}
          {/* POST-ORDER: Two-column layout                     */}
          {/* ═════════════════════════════════════════════════ */}
          {(reggStep === "streaming" || reggStep === "complete") && (
            <>
              {/* Loan diff warning */}
              {diffData?.has_action_required && (
                <Alert
                  severity="warning"
                  icon={<WarningAmberOutlined />}
                  sx={{ border: `1px solid ${amber[200]}` }}
                >
                  <Txt bold body2>
                    Deal data has changed since orders were placed
                  </Txt>
                  {diffData.diffs?.map((diff, i) =>
                    diff.changes?.map((change, j) => (
                      <Txt key={`${i}-${j}`} body2>
                        • {change.detail}
                        {change.action_hint && (
                          <Txt bold component="span" color="warning.main">
                            {" "}
                            — {change.action_hint}
                          </Txt>
                        )}
                      </Txt>
                    )),
                  )}
                </Alert>
              )}

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", lg: "3fr 2fr" },
                  gap: 3,
                  alignItems: "start",
                }}
              >
                {/* ── LEFT COLUMN ────────────────────────────── */}
                <Flx column gap={2.5}>
                  {/* Orders list */}
                  <SectionPaper title="Reggora Orders">
                    <Flx ac jb sx={{ mb: 2 }}>
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={
                          loading ? (
                            <CircularProgress size={14} />
                          ) : (
                            <RefreshOutlined />
                          )
                        }
                        onClick={handleFetchOrders}
                      >
                        Refresh Orders
                      </Button>
                    </Flx>

                    {reggOrders.length === 0 && (
                      <Txt
                        body2
                        color="text.secondary"
                        sx={{ fontStyle: "italic" }}
                      >
                        No orders loaded. Click Refresh to fetch.
                      </Txt>
                    )}

                    <Flx column gap={1.5}>
                      {reggOrders.map((order, i) => {
                        const ordId = order.external_order_id || "";
                        const s = order.status || "";
                        const isTerminal =
                          REGGORA_TERMINAL_STATUSES.includes(s);
                        return (
                          <Paper
                            key={i}
                            variant="outlined"
                            sx={{ p: 2, borderRadius: "8px" }}
                          >
                            <Flx ac jb>
                              <Flx column>
                                <Txt body2 sx={{ fontFamily: "monospace" }}>
                                  {ordId}
                                </Txt>
                                <Flx ac gap={1} sx={{ mt: 0.5 }}>
                                  <StatusChip status={s || "Unknown"} />
                                  {order.property_index !== undefined && (
                                    <Txt caption color="text.secondary">
                                      Property #{order.property_index}
                                    </Txt>
                                  )}
                                  {order.notifications && (
                                    <Txt caption color="text.secondary">
                                      {order.notifications.length}{" "}
                                      notification(s)
                                    </Txt>
                                  )}
                                </Flx>
                              </Flx>
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => setWebhookOrderId(ordId)}
                              >
                                Use in Simulator
                              </Button>
                            </Flx>

                            {/* Action buttons */}
                            <Flx gap={1} sx={{ mt: 1.5, flexWrap: "wrap" }}>
                              <Button
                                size="small"
                                variant={
                                  msgOrderId === ordId
                                    ? "contained"
                                    : "outlined"
                                }
                                startIcon={<ChatBubbleOutline />}
                                onClick={() => {
                                  const opening = msgOrderId !== ordId;
                                  setMsgOrderId(opening ? ordId : null);
                                  setMsgText("");
                                  setMsgConversation(null);
                                  if (opening) {
                                    setMsgConvLoading(true);
                                    fetchConversation({
                                      orderId: ordId,
                                      onSuccessFn: (data) => {
                                        setMsgConversation(data);
                                        setMsgConvLoading(false);
                                      },
                                      onFailFn: () => setMsgConvLoading(false),
                                    });
                                  }
                                }}
                              >
                                Message
                              </Button>
                              {!isTerminal && (
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="warning"
                                  startIcon={<PauseCircleOutlined />}
                                  onClick={() => {
                                    setHoldOrderId(
                                      holdOrderId === ordId ? null : ordId,
                                    );
                                    setHoldReason("");
                                  }}
                                >
                                  Hold
                                </Button>
                              )}
                            </Flx>

                            {/* Hold inline form */}
                            <Collapse in={holdOrderId === ordId}>
                              <Flx ac gap={1} sx={{ mt: 1.5 }}>
                                <MuiTextField
                                  size="small"
                                  fullWidth
                                  placeholder="Hold reason..."
                                  value={holdReason}
                                  onChange={(e) =>
                                    setHoldReason(e.target.value)
                                  }
                                />
                                <Button
                                  size="small"
                                  variant="contained"
                                  color="warning"
                                  disabled={!holdReason}
                                  onClick={() => handleHoldOrder(ordId)}
                                >
                                  Confirm
                                </Button>
                              </Flx>
                            </Collapse>

                            {/* Conversation inline */}
                            <Collapse in={msgOrderId === ordId}>
                              <Box
                                sx={{
                                  mt: 1.5,
                                  p: 1.5,
                                  backgroundColor: blue[50],
                                  borderRadius: 1,
                                  border: `1px solid ${blue[200]}`,
                                }}
                              >
                                <Txt body2 bold sx={{ mb: 1 }}>
                                  Conversation
                                </Txt>
                                {msgConvLoading ? (
                                  <Flx ac gap={1}>
                                    <CircularProgress size={14} />
                                    <Txt caption color="text.secondary">
                                      Loading...
                                    </Txt>
                                  </Flx>
                                ) : (
                                  <Box
                                    sx={{
                                      maxHeight: 200,
                                      overflowY: "auto",
                                      mb: 1.5,
                                    }}
                                  >
                                    {(() => {
                                      const conv =
                                        msgConversation?.conversation ||
                                        msgConversation ||
                                        {};
                                      const msgs =
                                        conv.messages ||
                                        conv.data?.messages ||
                                        [];
                                      if (msgs.length === 0)
                                        return (
                                          <Txt
                                            caption
                                            color="text.secondary"
                                            sx={{ fontStyle: "italic" }}
                                          >
                                            No messages yet.
                                          </Txt>
                                        );
                                      return msgs.map((m, mi) => (
                                        <Box
                                          key={mi}
                                          sx={{
                                            p: 1,
                                            mb: 0.5,
                                            backgroundColor: "#fff",
                                            borderRadius: 1,
                                            border: `1px solid ${blue[100]}`,
                                          }}
                                        >
                                          <Flx ac jb>
                                            <Txt
                                              caption
                                              bold
                                              sx={{ color: blue[700] }}
                                            >
                                              {m.sender_name ||
                                                m.sender ||
                                                "Unknown"}
                                            </Txt>
                                            {(m.created || m.created_at) && (
                                              <Txt
                                                caption
                                                color="text.secondary"
                                              >
                                                {new Date(
                                                  m.created || m.created_at,
                                                ).toLocaleString()}
                                              </Txt>
                                            )}
                                          </Flx>
                                          <Txt body2 sx={{ fontSize: "13px" }}>
                                            {m.message || m.text || ""}
                                          </Txt>
                                        </Box>
                                      ));
                                    })()}
                                  </Box>
                                )}
                                <Flx ac gap={1}>
                                  <MuiTextField
                                    size="small"
                                    fullWidth
                                    multiline
                                    maxRows={3}
                                    placeholder="Type a message..."
                                    value={msgText}
                                    onChange={(e) => setMsgText(e.target.value)}
                                    onKeyDown={(e) =>
                                      e.key === "Enter" &&
                                      !e.shiftKey &&
                                      handleSendMessage(ordId)
                                    }
                                  />
                                  <IconButton
                                    size="small"
                                    onClick={() => handleSendMessage(ordId)}
                                    disabled={!msgText.trim()}
                                    sx={{ color: blue[600] }}
                                  >
                                    <SendOutlined />
                                  </IconButton>
                                </Flx>
                              </Box>
                            </Collapse>
                          </Paper>
                        );
                      })}
                    </Flx>
                  </SectionPaper>

                  {/* Webhook Simulator */}
                  <SectionPaper title="Simulate Webhook">
                    <Flx column gap={2}>
                      {/* Order ID */}
                      <Flx column gap={0.5}>
                        <Txt body2 bold>
                          Order ID
                        </Txt>
                        {reggOrders.length > 0 ? (
                          <Flx gap={1}>
                            <MuiTextField
                              size="small"
                              select
                              fullWidth
                              value={webhookOrderId}
                              onChange={(e) =>
                                setWebhookOrderId(e.target.value)
                              }
                              SelectProps={{ native: true }}
                            >
                              <option value="">— Select Order —</option>
                              {reggOrders.map((o, i) => (
                                <option
                                  key={i}
                                  value={o.external_order_id || ""}
                                >
                                  {o.external_order_id} (Property #
                                  {o.property_index ?? i})
                                </option>
                              ))}
                            </MuiTextField>
                          </Flx>
                        ) : (
                          <MuiTextField
                            size="small"
                            fullWidth
                            value={webhookOrderId}
                            onChange={(e) => setWebhookOrderId(e.target.value)}
                            placeholder="reggora_order_id"
                            sx={{ fontFamily: "monospace" }}
                          />
                        )}
                      </Flx>

                      {/* Quick-fire buttons */}
                      {webhookOrderId && (
                        <Flx column gap={0.5}>
                          <Txt body2 bold>
                            Quick Examples
                          </Txt>
                          <Flx sx={{ flexWrap: "wrap", gap: 0.75 }}>
                            {REGGORA_EVENT_TYPES.map((evt) => {
                              const info = EVENT_ICON_MAP[evt] || {
                                icon: "📌",
                              };
                              const display = REGGORA_EVENT_DISPLAY[evt] || {
                                label: evt,
                              };
                              return (
                                <Chip
                                  key={evt}
                                  label={`${info.icon} ${display.label}`}
                                  size="small"
                                  variant="outlined"
                                  clickable
                                  onClick={() => handleSimulateWebhook(evt, {})}
                                  sx={{ fontSize: "11px" }}
                                />
                              );
                            })}
                          </Flx>
                        </Flx>
                      )}

                      {/* Manual builder */}
                      <Accordion title="Manual Webhook Builder">
                        <Flx column gap={1.5} sx={{ pt: 1 }}>
                          <Flx gap={2}>
                            <MuiTextField
                              size="small"
                              select
                              label="Event Type"
                              value={webhookEventType}
                              onChange={(e) =>
                                setWebhookEventType(e.target.value)
                              }
                              SelectProps={{ native: true }}
                              sx={{ minWidth: 200 }}
                            >
                              <option value="">— Select —</option>
                              {REGGORA_EVENT_TYPES.map((e) => (
                                <option key={e} value={e}>
                                  {e}
                                </option>
                              ))}
                            </MuiTextField>
                            {[
                              "status_update",
                              "order_created",
                              "order_updated",
                              "order_approved",
                            ].includes(webhookEventType) && (
                              <MuiTextField
                                size="small"
                                select
                                label="Status"
                                value={webhookStatus}
                                onChange={(e) =>
                                  setWebhookStatus(e.target.value)
                                }
                                SelectProps={{ native: true }}
                                sx={{ minWidth: 180 }}
                              >
                                <option value="">— Select —</option>
                                {REGGORA_STATUSES.map((s) => (
                                  <option key={s} value={s}>
                                    {s}
                                  </option>
                                ))}
                              </MuiTextField>
                            )}
                          </Flx>
                          {PAYMENT_EVENT_TYPES.has(webhookEventType) && (
                            <MuiTextField
                              size="small"
                              label="Payment Amount"
                              value={webhookPaymentAmount}
                              onChange={(e) =>
                                setWebhookPaymentAmount(e.target.value)
                              }
                              placeholder="550.00"
                            />
                          )}
                          {webhookEventType === "status_update" &&
                            webhookStatus === "Inspection Scheduled" && (
                              <MuiTextField
                                size="small"
                                type="datetime-local"
                                label="Inspection Date"
                                InputLabelProps={{ shrink: true }}
                                value={webhookInspectionDate}
                                onChange={(e) =>
                                  setWebhookInspectionDate(e.target.value)
                                }
                              />
                            )}
                          {(webhookEventType === "order_report_received" ||
                            webhookEventType === "order_ready_for_review") && (
                            <MuiTextField
                              size="small"
                              label="Submission Version"
                              value={webhookSubmissionVersion}
                              onChange={(e) =>
                                setWebhookSubmissionVersion(e.target.value)
                              }
                            />
                          )}
                          <Button
                            size="small"
                            variant="contained"
                            disabled={!webhookOrderId || !webhookEventType}
                            onClick={() => handleSimulateWebhook()}
                          >
                            Send Manual Webhook
                          </Button>
                        </Flx>
                      </Accordion>

                      {webhookResponse && (
                        <Box
                          sx={{
                            backgroundColor: grey[50],
                            borderRadius: 1,
                            p: 1.5,
                          }}
                        >
                          <Txt body2 bold sx={{ mb: 0.5 }}>
                            Response
                          </Txt>
                          <Box
                            component="pre"
                            sx={{
                              fontSize: "11px",
                              fontFamily: "monospace",
                              color: grey[600],
                              whiteSpace: "pre-wrap",
                            }}
                          >
                            {JSON.stringify(webhookResponse, null, 2)}
                          </Box>
                        </Box>
                      )}
                    </Flx>
                  </SectionPaper>
                </Flx>

                {/* ── RIGHT COLUMN ───────────────────────────── */}
                <CommentsHistoryColumn
                  notifications={allNotifications}
                  webhookEvents={webhookEvents}
                  contacts={dealContacts}
                  onRefresh={handleFetchOrders}
                  loading={loading}
                />
              </Box>
            </>
          )}
        </Flx>
      </Box>
    </Box>
  );
};

export default ReggoraAppraisalOrderScreen;
